import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowLeft, ChevronRight, Crown, Flag, ClipboardList, FileText, ShieldCheck, CheckCircle2, Utensils } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { VipSwitcher } from './CoachingPage';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Onboarding hub — the first things a new V.I.P. client completes.
// Items with an empty `url` render as "Coming soon" until Jill adds them.
const STEPS = [
  {
    key: 'lifestyle',
    icon: ClipboardList,
    title: 'The Vitality Potential Tool',
    description: 'A snapshot of your sleep, stress, nutrition and movement to set our starting point.',
    url: '/quiz/lifestyle-assessment',
  },
  {
    key: 'metabolic',
    icon: ClipboardList,
    title: 'Metabolic Typing Questionnaire',
    description: 'Discover the way of eating that best suits your body.',
    url: '/quiz/metabolic-rhythm',
  },
  {
    key: 'food-preferences',
    icon: Utensils,
    title: 'Food Preferences',
    description: 'Tick the foods you love or need to avoid so Jill can shape your recipes.',
    url: '/quiz/food-preferences',
  },
  {
    key: 'waiver',
    icon: ShieldCheck,
    title: 'Liability Waiver',
    description: 'Required before your first session.',
    url: '/programs/vip/liability-waiver',
  },
  {
    key: 'medical',
    icon: FileText,
    title: 'Health & Medical Disclosure',
    description: 'Helps Jill tailor your sessions safely.',
    url: '/programs/vip/medical-disclosure',
  },
];

const StartHerePage = () => {
  const { user, hasAccess, loading, refreshUser } = useAuth();
  const [status, setStatus] = useState(null);

  useEffect(() => {
    if (user?.id) refreshUser(user.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!user?.id) return;
    fetch(`${BACKEND_URL}/api/start-here/status/${user.id}?email=${encodeURIComponent(user.email || '')}`)
      .then((r) => r.json())
      .then(setStatus)
      .catch((e) => console.error(e));
  }, [user]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-stone-500 font-body">Loading…</div>
      </div>
    );
  }

  if (!user || !hasAccess('vip')) {
    return (
      <div className="min-h-screen bg-cream py-16 px-4" data-testid="start-here-access-gate">
        <div className="max-w-2xl mx-auto text-center">
          <Link to="/programs" className="inline-flex items-center gap-2 text-forest/70 hover:text-forest font-body text-sm mb-10">
            <ArrowLeft size={16} /> Back to Programs
          </Link>
          <div className="bg-[#f5efe4] border border-stone-200 rounded-2xl p-10 md:p-14">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-forest/10 text-forest mb-6">
              <Crown size={28} />
            </div>
            <p className="text-xs font-body text-[#8a6a5a] uppercase tracking-[0.25em] mb-3">Private Members Area</p>
            <h1 className="font-heading text-3xl md:text-4xl text-forest mb-4">Start Here</h1>
            <p className="text-stone-600 font-body leading-relaxed mb-8">
              A getting-started space for Jill's V.I.P. members & package holders.
            </p>
            <a
              href="mailto:heyjbennett@yahoo.ca?subject=V.I.P. Membership Inquiry"
              className="inline-flex items-center gap-2 bg-forest text-cream font-body font-medium px-6 py-3 rounded-full hover:bg-forest-dark transition-colors"
              data-testid="start-here-inquiry-link"
            >
              Inquire about V.I.P. <ChevronRight size={16} />
            </a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cream py-12 px-4" data-testid="start-here-page">
      <div className="max-w-3xl mx-auto">
        <Link to="/programs" className="inline-flex items-center gap-2 text-forest/70 hover:text-forest font-body text-sm mb-8">
          <ArrowLeft size={16} /> Back to Programs
        </Link>

        <VipSwitcher active="start-here" />

        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="mb-8">
          <div className="flex items-center gap-2 mb-3">
            <Flag size={16} className="text-[#8a6a5a]" />
            <p className="text-xs font-body text-[#8a6a5a] uppercase tracking-[0.25em]">Getting Started</p>
          </div>
          <h1 className="font-heading text-4xl md:text-5xl text-forest mb-3">Start Here</h1>
          <p className="text-stone-600 font-body leading-relaxed max-w-xl">
            Welcome! A few quick things to complete so we can begin with a clear picture of where you are.
          </p>
        </motion.div>

        {status && (() => {
          const done = STEPS.filter((s) => status[s.key]).length;
          const pct = Math.round((done / STEPS.length) * 100);
          return (
            <div className="mb-6 bg-white border border-stone-200 rounded-2xl p-4" data-testid="start-here-progress">
              <div className="flex justify-between items-baseline mb-2">
                <p className="font-body text-sm text-forest font-medium">Your onboarding progress</p>
                <p className="font-body text-xs text-stone-500" data-testid="start-here-progress-count">{done} of {STEPS.length} complete</p>
              </div>
              <div className="h-2 bg-stone-100 rounded-full overflow-hidden">
                <div className="h-full bg-forest transition-all" style={{ width: `${pct}%` }} data-testid="start-here-progress-bar" />
              </div>
              {done === STEPS.length && (
                <p className="font-body text-xs text-forest mt-2 flex items-center gap-1"><CheckCircle2 size={13} /> All done — you're ready to begin. Thank you!</p>
              )}
            </div>
          );
        })()}

        <ol className="space-y-3" data-testid="start-here-steps">
          {STEPS.map((step, i) => {
            const Icon = step.icon;
            const comingSoon = !step.url;
            const isDone = !!(status && status[step.key]);
            const inner = (
              <>
                <div className="flex items-start gap-4">
                  <span className={`inline-flex items-center justify-center w-10 h-10 rounded-full flex-shrink-0 ${isDone ? 'bg-forest text-cream' : 'bg-forest/10 text-forest'}`}>
                    {isDone ? <CheckCircle2 size={18} /> : <Icon size={18} />}
                  </span>
                  <div>
                    <p className="font-heading text-lg text-forest leading-tight flex items-center gap-2 flex-wrap">
                      <span className="text-[#8a6a5a] font-body text-sm">{i + 1}.</span> {step.title}
                      {comingSoon && (
                        <span className="text-[10px] font-body uppercase tracking-wider bg-stone-100 text-stone-400 rounded-full px-2 py-0.5">Coming soon</span>
                      )}
                      {isDone && (
                        <span className="text-[10px] font-body uppercase tracking-wider bg-forest/10 text-forest rounded-full px-2 py-0.5" data-testid={`start-here-done-${step.key}`}>Done</span>
                      )}
                    </p>
                    <p className="font-body text-sm text-stone-500 mt-0.5">{step.description}</p>
                  </div>
                </div>
                <span className={`inline-flex items-center gap-1 text-xs font-body whitespace-nowrap self-center ${comingSoon ? 'text-stone-300' : 'text-forest'}`}>
                  {comingSoon ? 'Soon' : isDone ? 'Review' : 'Start'} <ChevronRight size={13} />
                </span>
              </>
            );
            const cls = `flex items-center justify-between gap-4 p-4 rounded-2xl bg-white border transition-colors ${isDone ? 'border-forest/30' : 'border-stone-200'}`;
            return comingSoon ? (
              <li key={i} className={`${cls} opacity-70`} data-testid={`start-here-step-${i}`}>{inner}</li>
            ) : (
              <li key={i} data-testid={`start-here-step-${i}`}>
                <Link to={step.url} className={`${cls} hover:border-forest/40 hover:bg-stone-50`} data-testid={`start-here-link-${i}`}>
                  {inner}
                </Link>
              </li>
            );
          })}
        </ol>
      </div>
    </div>
  );
};

export default StartHerePage;
